import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import {
    hasHourlyActivity,
    type OperationsByHourData,
} from '../../domain/statisticsOperations';
import ChartPlaceholder from './ChartPlaceholder';

interface OperationsByHourChartProps {
    readonly data: readonly OperationsByHourData[];
    readonly loading?: boolean;
}

function OperationsByHourChart({
    data,
    loading = false,
}: OperationsByHourChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || !hasHourlyActivity(data)) {
        return <ChartPlaceholder loading={loading} hasData={hasHourlyActivity(data)} height={480} />;
    }

    const categories = data.map(d => `${d.hour}:00`);

    const options: Highcharts.Options = {
        chart: {
            type: 'bar',
            height: 480,
        },
        title: {
            text: undefined,
        },
        xAxis: {
            categories,
            title: {
                text: t('statistics.operations.hourOfDay', 'Hour of day'),
            },
        },
        yAxis: {
            min: 0,
            title: {
                text: t('statistics.operations.avgPerDay', 'Avg operations per day'),
            },
        },
        tooltip: {
            shared: true,
            valueDecimals: 1,
        },
        plotOptions: {
            bar: {
                borderWidth: 0,
                groupPadding: 0.1,
            },
        },
        series: [
            {
                type: 'bar',
                name: t('statistics.operations.contracts', 'Contracts'),
                data: data.map(d => d.contracts),
                color: '#1890ff',
            },
            {
                type: 'bar',
                name: t('statistics.operations.redemptions', 'Redemptions'),
                data: data.map(d => d.redemptions),
                color: '#52c41a',
            },
            {
                type: 'bar',
                name: t('statistics.operations.prolongations', 'Prolongations'),
                data: data.map(d => d.prolongations),
                color: '#faad14',
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default OperationsByHourChart;
